import type { SaleLineInput } from '@gl/shared';
import { useReducer } from 'react';

export interface CartLine {
  key: string;
  itemId?: string;
  variantId?: string;
  name: string;
  variantName?: string;
  unitPriceCents: number;
  quantity: number;
  /** Línea libre o con precio cambiado: se manda `unitPriceCents` (solo OWNER/MANAGER). */
  customPrice?: boolean;
}

export interface CartState {
  lines: CartLine[];
  discountCents: number;
}

type Action =
  | { type: 'add'; line: Omit<CartLine, 'quantity' | 'key'> & { key?: string } }
  | { type: 'qty'; key: string; delta: number }
  | { type: 'remove'; key: string }
  | { type: 'discount'; cents: number }
  | { type: 'clear' };

const EMPTY: CartState = { lines: [], discountCents: 0 };

function reducer(state: CartState, action: Action): CartState {
  switch (action.type) {
    case 'add': {
      const key = action.line.key ?? `${action.line.itemId ?? ''}:${action.line.variantId ?? ''}`;
      const existing = state.lines.find((l) => l.key === key);
      if (existing) {
        return { ...state, lines: state.lines.map((l) => (l.key === key ? { ...l, quantity: Math.min(999, l.quantity + 1) } : l)) };
      }
      return { ...state, lines: [...state.lines, { ...action.line, key, quantity: 1 }] };
    }
    case 'qty':
      return {
        ...state,
        lines: state.lines
          .map((l) => (l.key === action.key ? { ...l, quantity: Math.min(999, l.quantity + action.delta) } : l))
          .filter((l) => l.quantity > 0),
      };
    case 'remove':
      return { ...state, lines: state.lines.filter((l) => l.key !== action.key) };
    case 'discount':
      return { ...state, discountCents: Math.max(0, action.cents) };
    case 'clear':
      return EMPTY;
  }
}

export function useCart() {
  const [state, dispatch] = useReducer(reducer, EMPTY);
  const subtotalCents = state.lines.reduce((a, l) => a + l.unitPriceCents * l.quantity, 0);
  const count = state.lines.reduce((a, l) => a + l.quantity, 0);
  const totalCents = Math.max(0, subtotalCents - state.discountCents);
  return { ...state, subtotalCents, totalCents, count, dispatch };
}

export type Cart = ReturnType<typeof useCart>;

export function toSaleLines(lines: CartLine[]): SaleLineInput[] {
  return lines.map((l) =>
    l.itemId || l.variantId
      ? {
          itemId: l.itemId,
          variantId: l.variantId,
          quantity: l.quantity,
          ...(l.customPrice ? { unitPriceCents: l.unitPriceCents } : {}),
        }
      : { name: l.name, quantity: l.quantity, unitPriceCents: l.unitPriceCents },
  );
}
